import imageCompression from 'browser-image-compression';

/**
 * Options de compression par défaut
 * - maxSizeMB: Taille maximale en MB (1MB pour optimisation web)
 * - maxWidthOrHeight: Dimension maximale (1920px pour haute qualité)
 * - useWebWorker: Utilise un Web Worker pour ne pas bloquer l'UI
 * - fileType: Convertir en JPEG pour meilleure compression (sauf si transparent)
 */
const DEFAULT_OPTIONS = {
  maxSizeMB: 1,
  maxWidthOrHeight: 1920,
  useWebWorker: true,
  initialQuality: 0.8,
};

/**
 * Compresse une image avant l'upload
 *
 * @param file - Le fichier image à compresser
 * @param options - Options de compression personnalisées (optionnel)
 * @returns Promise avec le fichier compressé
 *
 * @example
 * const compressedFile = await compressImage(file);
 * // Le fichier est maintenant optimisé pour l'upload
 */
export async function compressImage(
  file: File,
  options: Partial<typeof DEFAULT_OPTIONS> = {}
): Promise<File> {
  const compressionOptions = {
    ...DEFAULT_OPTIONS,
    ...options,
  };

  try {
    // Si le fichier fait déjà moins de 1MB, on le retourne tel quel
    if (file.size <= compressionOptions.maxSizeMB * 1024 * 1024) {
      return file;
    }

    const compressedBlob = await imageCompression(file, compressionOptions);

    // Convertir le Blob en File pour maintenir le nom et le type
    const compressedFile = new File(
      [compressedBlob],
      file.name,
      { type: compressedBlob.type || file.type }
    );

    return compressedFile;
  } catch (error) {
    console.error('Erreur lors de la compression de l\'image:', error);
    // En cas d'erreur, retourner le fichier original
    return file;
  }
}

/**
 * Compresse plusieurs images en parallèle
 *
 * @param files - Tableau de fichiers images à compresser
 * @param options - Options de compression personnalisées (optionnel)
 * @returns Promise avec le tableau de fichiers compressés
 *
 * @example
 * const compressedFiles = await compressImages(filesArray);
 */
export async function compressImages(
  files: File[],
  options: Partial<typeof DEFAULT_OPTIONS> = {}
): Promise<File[]> {
  return Promise.all(files.map((file) => compressImage(file, options)));
}
